/** Contabilidad → Plan de cuentas del emisor, con búsqueda y apertura de subcuentas. */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, Loader2, Plus } from 'lucide-react'
import { api } from '../../lib/api'
import { usePgcIssuer, useCanSeeLedger } from '../../lib/ctb'
import CtbHeader from '../../components/ctb/CtbHeader'
import AccountMapCard from '../../components/ctb/AccountMapCard'
import { apiMessage } from '../../components/ctb/EntryModal'
import { Note, selectCls, td, th } from '../../components/accounting/common'
import type { CtbAccountDto, CtbStatusDto } from '@toga/shared'

const NATURE: Record<string, string> = {
  ASSET: 'Activo',
  LIABILITY: 'Pasivo',
  EQUITY: 'Patrimonio neto',
  INCOME: 'Ingreso',
  EXPENSE: 'Gasto',
}

export default function ChartPage() {
  const canSee = useCanSeeLedger()
  const { pgc, issuerId, setIssuerId, loaded } = usePgcIssuer()
  const [items, setItems] = useState<CtbAccountDto[] | null>(null)
  const [status, setStatus] = useState<CtbStatusDto | null>(null)
  const [q, setQ] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sub, setSub] = useState<{ parent: CtbAccountDto; name: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!issuerId) return
    try {
      const [a, s] = await Promise.all([
        api.get<{ items: CtbAccountDto[] }>(`/ctb/accounts?issuerId=${issuerId}`),
        api.get<CtbStatusDto>(`/ctb/status?issuerId=${issuerId}`),
      ])
      setItems(a.items)
      setStatus(s)
    } catch (err) {
      setError(apiMessage(err, 'No se pudo cargar el plan'))
    }
  }, [issuerId])

  useEffect(() => {
    void load()
  }, [load])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!items) return []
    return needle
      ? items.filter((a) => a.code.startsWith(needle) || a.name.toLowerCase().includes(needle))
      : items
  }, [items, q])

  const openSub = async () => {
    if (!sub) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/ctb/accounts/subaccount', {
        issuerId,
        parentCode: sub.parent.code,
        name: sub.name,
      })
      setSub(null)
      await load()
    } catch (err) {
      setError(apiMessage(err, 'No se pudo abrir la subcuenta'))
    } finally {
      setBusy(false)
    }
  }

  if (!canSee)
    return (
      <p className="t-muted text-sm">El plan de cuentas es solo para administradores y abogados.</p>
    )

  return (
    <div className="space-y-5 animate-slideUp">
      <CtbHeader
        title="Contabilidad · Plan de cuentas"
        description="El plan PGC de PYMES del emisor (una copia: el maestro no se toca) y sus subcuentas. Al abrir una subcuenta, la cuenta madre deja de admitir apuntes directos."
        pgc={pgc}
        loaded={loaded}
        issuerId={issuerId}
        onIssuer={setIssuerId}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por código o nombre…"
          className={`${selectCls} w-56`}
          aria-label="Buscar"
        />
        {status && (
          <span className="text-xs t-muted">
            {status.accountsCount} cuentas · {status.entriesCount} asientos · ejercicios{' '}
            {status.years.map((y) => y.startsOn.slice(0, 4)).join(', ')}
          </span>
        )}
      </CtbHeader>

      {error && (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {issuerId && <AccountMapCard issuerId={issuerId} />}

      {issuerId && (
        <section className="surface overflow-hidden">
          {!items ? (
            <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Cargando…
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[var(--bg-surface,#0b1220)]">
                  <tr className="border-b border-base">
                    <th className={th}>Código</th>
                    <th className={th}>Nombre</th>
                    <th className={th}>Naturaleza</th>
                    <th className={th}>Saldo normal</th>
                    <th className={th}>Estado</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((a) => (
                    <tr key={a.id} className="border-b border-base/50 hover:bg-hover/40">
                      <td className={`${td} font-mono`}>{a.code}</td>
                      <td className={td}>{a.name}</td>
                      <td className={`${td} text-xs`}>{NATURE[a.nature]}</td>
                      <td className={`${td} text-xs`}>
                        {a.normalBalance === 'DEBIT' ? 'Deudor' : 'Acreedor'}
                      </td>
                      <td className={`${td} text-[11px] t-faint`}>
                        {a.isSystem && 'Del sistema · '}
                        {!a.isPostable && 'Solo agrupa · '}
                        {a.blocked && 'Bloqueada'}
                      </td>
                      <td className={`${td} text-right`}>
                        {a.code.length < (status?.subaccountDigits ?? 7) && (
                          <button
                            type="button"
                            onClick={() => setSub({ parent: a, name: '' })}
                            className="text-xs text-toga-300 hover:underline inline-flex items-center gap-1"
                          >
                            <Plus size={12} /> Subcuenta
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Note>
            Las subcuentas llevan {status?.subaccountDigits ?? 7} dígitos (por ejemplo 4300001 para
            un cliente). El plan está sin contrastar con el BOE: la gestoría debe revisarlo antes
            del primer cierre.
          </Note>
        </section>
      )}

      {sub &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4"
            onClick={() => setSub(null)}
          >
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => {
                e.preventDefault()
                void openSub()
              }}
              className="w-full max-w-md surface-deep p-5 space-y-3"
            >
              <h3 className="text-base font-semibold t-primary">
                Nueva subcuenta de {sub.parent.code}
              </h3>
              <p className="text-xs t-muted">
                {sub.parent.name}. Se numera sola con el siguiente correlativo.
              </p>
              <input
                autoFocus
                value={sub.name}
                onChange={(e) => setSub({ ...sub, name: e.target.value })}
                placeholder="Nombre (p. ej. el cliente)"
                maxLength={200}
                required
                className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSub(null)}
                  className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busy || !sub.name.trim()}
                  className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50"
                >
                  Abrir subcuenta
                </button>
              </div>
            </form>
          </div>,
          document.body
        )}
    </div>
  )
}
